
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Target, Zap, Calendar, Users, Heart, Play, CheckCircle, User, Compass, Clipboard, BookOpen, Activity, MessageSquare } from "lucide-react";
import { ClientPageLayout } from "@/components/client/ClientPageLayout";
import { LoadRatioChartTitle, LoadRatioWeekChart, type LoadRatioChartPoint } from "@/components/client/LoadRatioWeekChart";
import { Link, useNavigate } from "react-router-dom";
import ProfileIconLink from "@/components/ProfileIconLink";
import { useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useEnforceLegalApproval } from "@/hooks/use-enforce-legal";
import { listWorkoutAssigned, type WorkoutAssigned } from "@/integrations/backend/workout-assigned";
import { parseYYYYMMDDToLocalDate } from "@/lib/date-only";
import { fetchDashboardMetrics, ensurePlanPdf } from "@/integrations/backend/dashboard";
import { listClientAssessments, type ClientAssessment } from "@/integrations/backend/assessments";
import { fetchWeeklySummary } from "@/integrations/backend/weekly-summary";
import { format } from "date-fns";

const ClientPortal = () => {
  useEnforceLegalApproval();
  const { profile, isClient, loading, sessionChecked } = useAuth();
  const navigate = useNavigate();
  const [activeWorkout, setActiveWorkout] = useState(null);
  const [loadRatioSeries, setLoadRatioSeries] = useState<LoadRatioChartPoint[]>([]);
  const [loadRatioWeekStart, setLoadRatioWeekStart] = useState<string | null>(null);
  const [totalLoggedWorkouts, setTotalLoggedWorkouts] = useState<number>(0);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [workoutAssignments, setWorkoutAssignments] = useState<WorkoutAssigned[]>([]);
  const [loadingWorkouts, setLoadingWorkouts] = useState(false);
  const [featuredAssessments, setFeaturedAssessments] = useState<ClientAssessment[]>([]);
  const [featuredAssessmentsLoading, setFeaturedAssessmentsLoading] = useState(false);
  const [featuredAssessmentsError, setFeaturedAssessmentsError] = useState<string | null>(null);
  const [weeklyGoal, setWeeklyGoal] = useState<number | null>(null);
  const [weeklyCompleted, setWeeklyCompleted] = useState<number>(0);
  const [weeklyStreakWeeks, setWeeklyStreakWeeks] = useState<number>(0);
  const [weeklySummaryLoading, setWeeklySummaryLoading] = useState(false);

  const openWorkoutGoalHistory = () => {
    navigate("/client-plan?tab=goals&openWorkoutGoalHistory=1");
  };
  

  // Trophies/Achievements removed

  // Format date relatively (Today, Tomorrow, Wednesday, Wednesday, Dec 1st)
  const formatRelativeDate = (dateString: string): string => {
    const workoutDate = parseYYYYMMDDToLocalDate(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    workoutDate.setHours(0, 0, 0, 0);
    
    const diffTime = workoutDate.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays === -1) return "Yesterday";
    if (diffDays > 1 && diffDays <= 7) {
      return workoutDate.toLocaleDateString('en-US', { weekday: 'long' });
    }
    // For dates more than a week away, show "Wednesday, Dec 1st" format
    return workoutDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Filter scheduled workouts (date >= today, not completed)
  const scheduledWorkouts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return workoutAssignments
      .filter((w) => {
        const workoutDate = parseYYYYMMDDToLocalDate(w.date);
        workoutDate.setHours(0, 0, 0, 0);
        const isCompleted = w.completed_workout !== null;
        return workoutDate >= today && !isCompleted;
      })
      .sort((a, b) => {
        // Sort by date ascending (oldest first)
        const dateA = parseYYYYMMDDToLocalDate(a.date).getTime();
        const dateB = parseYYYYMMDDToLocalDate(b.date).getTime();
        return dateA - dateB;
      })
      .map((w) => ({
        id: w.id,
        name: w.name,
        day: formatRelativeDate(w.date),
        duration: `${w.duration_minutes} min`
      }));
  }, [workoutAssignments]);

  // Get the next incomplete workout (first scheduled workout)
  const nextWorkout = useMemo(() => {
    if (scheduledWorkouts.length === 0) return null;
    const workout = workoutAssignments.find(w => w.id === scheduledWorkouts[0].id);
    return workout || null;
  }, [scheduledWorkouts, workoutAssignments]);

  // Get all exercise names and sets from the next workout
  const workoutExercises = useMemo(() => {
    if (!nextWorkout) return [];
    const exercises: Array<{ name: string; sets: number }> = [];
    for (const grouping of nextWorkout.groupings || []) {
      for (const exercise of grouping.exercises || []) {
        exercises.push({
          name: exercise.exercise_name,
          sets: exercise.sets || grouping.sets || 0
        });
      }
    }
    return exercises;
  }, [nextWorkout]);

  // Fetch scheduled workouts for the current client
  useEffect(() => {
    if (!profile?.id) return;
    
    let active = true;
    (async () => {
      try {
        setLoadingWorkouts(true);
        const assignments = await listWorkoutAssigned(profile.id);
        if (active) {
          setWorkoutAssignments(assignments);
        }
      } catch (error) {
        console.error("Failed to load scheduled workouts", error);
        if (active) {
          setWorkoutAssignments([]);
        }
      } finally {
        if (active) {
          setLoadingWorkouts(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id]);

  // Fire-and-forget: ensure plan PDF is generated if not yet done (runs async, non-blocking)
  useEffect(() => {
    if (!profile?.id || !isClient) return;
    ensurePlanPdf();
  }, [profile?.id, isClient]);

  // Fetch dashboard metrics (DB-backed): totals + load ratio series for this week
  useEffect(() => {
    if (!profile?.id) return;
    let active = true;
    (async () => {
      try {
        setMetricsLoading(true);
        const data = await fetchDashboardMetrics();
        if (!active) return;
        setTotalPoints(data.total_points || 0);
        setTotalLoggedWorkouts(data.total_logged_workouts || 0);
        setLoadRatioWeekStart(data.load_ratio_week_start ?? null);
        setLoadRatioSeries(
          (data.load_ratio_series || []).map((p) => ({
            date: p.date,
            label: p.label,
            value: p.value ?? 0,
          }))
        );
      } catch (e) {
        console.error("Failed to load dashboard metrics", e);
        if (!active) return;
        setTotalPoints(0);
        setTotalLoggedWorkouts(0);
        setLoadRatioWeekStart(null);
        setLoadRatioSeries([]);
      } finally {
        if (active) setMetricsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id, isClient, loading, sessionChecked]);

  // Fetch newest assessments (DB-backed) for the Featured Assessments module
  useEffect(() => {
    if (!profile?.id) return;
    let active = true;
    (async () => {
      try {
        setFeaturedAssessmentsLoading(true);
        setFeaturedAssessmentsError(null);
        const rows = await listClientAssessments();
        if (!active) return;
        setFeaturedAssessments(rows.slice(0, 3));
      } catch (e) {
        if (!active) return;
        setFeaturedAssessments([]);
        setFeaturedAssessmentsError(e instanceof Error ? e.message : "Failed to load assessments");
      } finally {
        if (active) setFeaturedAssessmentsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id]);

  // Fetch weekly goal + streak (DB-backed)
  useEffect(() => {
    if (!profile?.id) return;
    let active = true;
    (async () => {
      try {
        setWeeklySummaryLoading(true);
        const s = await fetchWeeklySummary();
        if (!active) return;
        setWeeklyGoal(s.weekly_goal);
        setWeeklyCompleted(s.current_week?.completed ?? 0);
        setWeeklyStreakWeeks(s.current_streak_weeks ?? 0);
      } catch (e) {
        console.error("Failed to load weekly summary", e);
        if (!active) return;
        setWeeklyGoal(null);
        setWeeklyCompleted(0);
        setWeeklyStreakWeeks(0);
      } finally {
        if (active) setWeeklySummaryLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [profile?.id]);

  const loadRatioWeekLabel = useMemo(() => {
    if (!loadRatioWeekStart) return null;
    const start = parseYYYYMMDDToLocalDate(loadRatioWeekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `Week of ${format(start, "MMM d")} – ${format(end, "MMM d")}`;
  }, [loadRatioWeekStart]);

  // fitness metrics removed

  return (
    <ClientPageLayout
      activeTab="home"
      header={
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h1 className="text-2xl font-bold flex flex-wrap items-center gap-2">
              <span>Hey, {profile?.first_name?.trim() || "there"}!</span>
              <button
                type="button"
                onClick={openWorkoutGoalHistory}
                className="text-sm bg-primary text-black py-1 px-3 rounded-full flex items-center font-semibold hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                aria-label="View your workout goal history"
              >
                <span className="mr-1">🔥</span>
                <span>{weeklySummaryLoading ? "—" : `${weeklyStreakWeeks}`} week streak</span>
              </button>
            </h1>
            <ProfileIconLink />
          </div>

          <button
            type="button"
            onClick={openWorkoutGoalHistory}
            className="w-full text-left bg-dark-slate/50 backdrop-blur-sm rounded-2xl p-4 border border-primary/20 hover:border-primary/40 hover:bg-dark-slate/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label="View this week's goal and workout goal history"
          >
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">This week's goal</span>
              <span className="text-xs bg-primary text-black rounded-full px-3 py-1 font-semibold">
                {weeklySummaryLoading
                  ? "—"
                  : `${weeklyCompleted}/${weeklyGoal ?? "—"} workouts`}
              </span>
            </div>
            <Progress
              value={
                weeklyGoal
                  ? Math.min(100, (weeklyCompleted / weeklyGoal) * 100)
                  : 0
              }
              className="h-3"
            />
          </button>
        </>
      }
    >
        {/* Next Workout */}
        {nextWorkout ? (
          <Card className="border-primary/20 shadow-neon-glow overflow-hidden bg-dark-card hover:scale-[1.02] transition-transform">
            <div className="gradient-primary text-black p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold">Your Next Workout for {formatRelativeDate(nextWorkout.date)}</h3>
                  <p className="opacity-80 font-medium mt-1">{nextWorkout.name}</p>
                  {workoutExercises.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {workoutExercises.slice(0, 3).map((ex, idx) => (
                        <div key={idx} className="text-sm opacity-90">
                          {ex.name} • {ex.sets} {ex.sets === 1 ? 'set' : 'sets'}
                        </div>
                      ))}
                      {workoutExercises.length > 3 && (
                        <div className="text-sm opacity-75">
                          +{workoutExercises.length - 3} more {workoutExercises.length - 3 === 1 ? 'exercise' : 'exercises'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="sm:text-right sm:ml-4 shrink-0">
                  <div className="text-2xl font-bold">{nextWorkout.duration_minutes} min</div>
                  <div className="text-sm opacity-80">{workoutExercises.length} {workoutExercises.length === 1 ? 'exercise' : 'exercises'}</div>
                </div>
              </div>
              
              <Button 
                className="w-full bg-dark-bg text-white hover:bg-dark-slate font-semibold py-4 text-lg shadow-lg border-2 border-dark-bg" 
                size="lg"
                onClick={() => navigate("/start-workout")}
              >
                <Play className="w-5 h-5 mr-2" />
                Start Workout
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="border-primary/20 shadow-neon-glow overflow-hidden bg-dark-card">
            <div className="gradient-primary text-black p-6">
              <div className="text-center">
                <h3 className="text-xl font-bold mb-2">No Upcoming Workouts</h3>
                <p className="opacity-80">Check back later for your next workout assignment.</p>
              </div>
            </div>
          </Card>
        )}

        {/* Quick Stats (trophies removed) */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-primary/20 shadow-card bg-dark-card hover:shadow-neon-glow hover:scale-105 transition-all">
            <CardContent className="p-4 sm:p-6 text-center">
              <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-black" />
              </div>
              <div className="text-2xl font-bold text-white">
                {metricsLoading ? "—" : totalPoints.toLocaleString()}
              </div>
              <div className="text-sm text-gray-light">Load Points</div>
            </CardContent>
          </Card>
          <Card className="border-primary/20 shadow-card bg-dark-card hover:shadow-neon-glow hover:scale-105 transition-all">
            <CardContent className="p-4 sm:p-6 text-center">
              <div className="w-12 h-12 bg-strength rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="text-2xl font-bold text-white">{totalLoggedWorkouts}</div>
              <div className="text-sm text-gray-light">Total Logged Workouts</div>
            </CardContent>
          </Card>
        </div>

        {/* Load ratio chart (7-day vs 28-day average, current week) */}
        <Card className="border-primary/20 shadow-card overflow-hidden bg-dark-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-white">
              <LoadRatioChartTitle />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LoadRatioWeekChart
              data={loadRatioSeries}
              loading={metricsLoading}
              weekRangeLabel={loadRatioWeekLabel}
              gradientId="homeLoadRatioArea"
            />
          </CardContent>
        </Card>

        {/* Quick Actions - New section added before Upcoming Workouts */}
        <Card className="border-primary/20 shadow-card bg-dark-card">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center space-x-2 text-white">
              <Compass className="w-5 h-5 text-primary" />
              <span>Explore Workouts</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/explore#workouts">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1 border-primary/30 hover:bg-primary hover:text-black hover:border-primary">
                  <Target className="w-5 h-5" />
                  <span className="text-xs">Browse Templates</span>
                </Button>
              </Link>
              <Link to="/chat">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1 border-primary/30 hover:bg-primary hover:text-black hover:border-primary">
                  <MessageSquare className="w-5 h-5" />
                  <span className="text-xs">Chat with Trainer</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Featured Assessments Section */}
        <Card className="border-primary/20 shadow-card bg-dark-card">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2 text-white">
                <Clipboard className="w-5 h-5 text-primary" />
                <span>Featured Assessments</span>
              </CardTitle>
              <Link to="/assessments">
                <Button size="sm" variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-black">
                  View All
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-light">Track your progress and set new goals</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4">
              {featuredAssessmentsError ? (
                <div className="text-sm text-red-200 py-2">Failed to load assessments: {featuredAssessmentsError}</div>
              ) : null}

              {featuredAssessmentsLoading ? (
                <div className="text-sm text-gray-light py-2">Loading assessments…</div>
              ) : featuredAssessments.length === 0 ? (
                <div className="text-sm text-gray-light py-2">No assessments yet.</div>
              ) : (
                featuredAssessments.map((assessment, idx) => {
                  const hasTypeform = Boolean((assessment.typeform_url || "").trim());
                  const iconBg = idx % 3 === 0 ? "bg-conditioning" : idx % 3 === 1 ? "bg-strength" : "bg-cardio";
                  return (
                    <div
                      key={assessment.id}
                      className="border border-primary/20 rounded-xl p-4 sm:p-5 bg-dark-slate hover:bg-dark-slate/80 transition-all"
                    >
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className={`w-12 h-12 sm:w-14 sm:h-14 ${iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Clipboard className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                            <h4 className="font-medium text-sm sm:text-base text-white truncate">{assessment.name}</h4>
                            <Badge variant="default" className="text-xs w-fit bg-primary/20 text-primary border-primary/30">
                              Assessment
                            </Badge>
                          </div>
                          {assessment.description ? (
                            <p className="text-xs sm:text-sm text-gray-light mb-3">{assessment.description}</p>
                          ) : (
                            <p className="text-xs sm:text-sm text-gray-light mb-3">—</p>
                          )}
                          <div className="flex items-center justify-end">
                            {hasTypeform ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-xs sm:text-sm px-3 py-2 h-auto min-h-[32px] sm:min-h-[36px] text-primary hover:text-black hover:bg-primary"
                                onClick={() => navigate(`/assessments/${assessment.id}`)}
                              >
                                Start
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" disabled className="text-xs sm:text-sm px-3 py-2 h-auto min-h-[32px] sm:min-h-[36px]">
                                Coming soon
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Workouts */}
        <Card className="border-primary/20 shadow-card bg-dark-card">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center space-x-2 text-white">
              <Calendar className="w-5 h-5 text-primary" />
              <span>Upcoming Workouts</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loadingWorkouts ? (
              <div className="text-sm text-gray-light py-4 text-center">Loading workouts...</div>
            ) : scheduledWorkouts.length === 0 ? (
              <div className="text-sm text-gray-light py-4 text-center">No upcoming workouts scheduled.</div>
            ) : (
              scheduledWorkouts.map(workout => (
                <div key={workout.id} className="flex items-center justify-between p-4 border border-primary/20 rounded-xl bg-dark-slate hover:bg-dark-slate/80 hover:border-primary/40 transition-all">
                  <div>
                    <div className="font-medium text-white">{workout.name}</div>
                    <div className="text-sm text-gray-light">{workout.day}</div>
                  </div>
                  <div className="text-right">
                    <Badge variant="default" className="bg-primary/20 text-primary border-primary/30">
                      {workout.duration}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Insights module removed */}
    </ClientPageLayout>
  );
};

export default ClientPortal;
